// NotificationPanel.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Drawer,
  List,
  Tag,
  Typography,
  Spin,
  Empty,
  Badge,
  Button,
  Space,
  Divider,
  Alert,
  Tooltip,
  Modal,
  Card,
  Tabs,
  Statistic,
  Row,
  Col,
  Descriptions,
  message
} from 'antd';
import {
  BellOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  ReloadOutlined,
  CheckOutlined,
  DeleteOutlined,
  FileTextOutlined,
  UserOutlined,
  ApartmentOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Text, Paragraph } = Typography;
const { TabPane } = Tabs;

const API_BASE = process.env.REACT_APP_API_BASE_URL + '/api';

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000
});

api.interceptors.request.use(config => {
  const token = sessionStorage.getItem('token');
  const userRole = sessionStorage.getItem('userRole') || 'ULB';
  const userId = sessionStorage.getItem('userId');
  const userDivision = sessionStorage.getItem('division_id') || sessionStorage.getItem('division');
  const userDistrict = sessionStorage.getItem('district_id') || sessionStorage.getItem('district');
  const userDeskId = sessionStorage.getItem('desk_id') || sessionStorage.getItem('deskId');

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers['x-user-role'] = userRole;
  if (userId && userId !== 'null') config.headers['x-user-id'] = userId;
  if (userDivision && userDivision !== 'null') config.headers['x-user-division'] = userDivision;
  if (userDistrict && userDistrict !== 'null') config.headers['x-user-district'] = userDistrict;
  if (userDeskId && userDeskId !== 'null') config.headers['x-user-desk-id'] = userDeskId;

  return config;
});

const NotificationPanel = ({ visible, onClose, onNotificationClick, userInfo }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({ total: 0, approved: 0, rejected: 0, pending: 0, escalated: 0 });
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const intervalRef = useRef(null);

  const loadNotifications = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await api.get('/workflow/notifications');
      if (response.data.success) {
        setNotifications(response.data.data || []);
        setSummary(response.data.summary || { total: 0, approved: 0, rejected: 0, pending: 0, escalated: 0 });
      }
    } catch (error) {
      console.error('Failed to load notifications:', error);
      if (!silent) {
        message.error('Failed to load notifications');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (visible && autoRefresh) {
      loadNotifications(true);
      intervalRef.current = setInterval(() => {
        loadNotifications(true);
      }, 30000);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [visible, autoRefresh, loadNotifications]);

  useEffect(() => {
    if (visible) {
      loadNotifications();
    }
  }, [visible, loadNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await api.post(`/workflow/notifications/${notificationId}/read`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    setSelectedNotification(notification);
    setDetailModalVisible(true);
    markAsRead(notification.id);
    if (onNotificationClick) {
      onNotificationClick(notification);
    }
  };

  const getIconByType = (type) => {
    switch (type) {
      case 'Approved': return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} />;
      case 'Rejected': return <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 20 }} />;
      case 'Re-Approved': return <CheckCircleOutlined style={{ color: '#faad14', fontSize: 20 }} />;
      case 'Pending': return <ClockCircleOutlined style={{ color: '#1890ff', fontSize: 20 }} />;
      case 'Escalated': return <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 20 }} />;
      default: return <BellOutlined style={{ color: '#666', fontSize: 20 }} />;
    }
  };

  const getTagByType = (type) => {
    const config = {
      'Approved': { color: 'success', icon: <CheckCircleOutlined /> },
      'Rejected': { color: 'error', icon: <CloseCircleOutlined /> },
      'Re-Approved': { color: 'warning', icon: <CheckCircleOutlined /> },
      'Pending': { color: 'processing', icon: <ClockCircleOutlined /> },
      'Escalated': { color: 'orange', icon: <ExclamationCircleOutlined /> }
    };
    const cfg = config[type] || { color: 'default', icon: <BellOutlined /> };
    return <Tag color={cfg.color} icon={cfg.icon}>{type}</Tag>;
  };

  const filteredByType = (type) => {
    if (type === 'all') return notifications;
    return notifications.filter(n => n.type === type);
  };

  const renderNotificationList = (items) => (
    <List
      itemLayout="vertical"
      dataSource={items}
      renderItem={(item) => (
        <List.Item
          key={item.id}
          className="notification-item"
          onClick={() => handleNotificationClick(item)}
          style={{
            cursor: 'pointer',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '8px',
            background: '#fafafa',
            transition: 'all 0.3s',
            borderLeft: `4px solid ${item.type === 'Approved' ? '#52c41a' : item.type === 'Rejected' ? '#ff4d4f' : '#1890ff'}`
          }}
        >
          <List.Item.Meta
            avatar={getIconByType(item.type)}
            title={
              <Space direction="vertical" size={0} style={{ width: '100%' }}>
                <Space wrap>
                  <Text strong>{item.title}</Text>
                  {getTagByType(item.type)}
                </Space>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {item.formName && <span><FileTextOutlined /> {item.formName} </span>}
                  {item.submissionId && <span>| Submission #{item.submissionId}</span>}
                </Text>
              </Space>
            }
            description={
              <div>
                <Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 8 }}>
                  {item.message}
                </Paragraph>
                <Space size="small" style={{ fontSize: '12px', color: '#999' }}>
                  <span>{new Date(item.createdAt).toLocaleString()}</span>
                  {item.actionPerformedBy && (
                    <span><UserOutlined /> {item.actionPerformedBy}</span>
                  )}
                  {item.actionPerformedByRole && (
                    <Tag size="small">{item.actionPerformedByRole}</Tag>
                  )}
                </Space>
              </div>
            }
          />
        </List.Item>
      )}
    />
  );

  return (
    <>
      <Drawer
        title={
          <Space>
            <BellOutlined />
            <span>Notifications</span>
            <Badge count={notifications.length} style={{ backgroundColor: '#1890ff' }} />
          </Space>
        }
        placement="right"
        onClose={onClose}
        open={visible}
        width={450}
        extra={
          <Space>
            <Tooltip title="Auto-refresh (30s)">
              <Button
                size="small"
                icon={<ReloadOutlined spin={autoRefresh && visible} />}
                onClick={() => loadNotifications()}
              />
            </Tooltip>
            <Tooltip title="Mark all as read">
              <Button
                size="small"
                icon={<CheckOutlined />}
                onClick={() => {
                  notifications.forEach(n => markAsRead(n.id));
                }}
              />
            </Tooltip>
          </Space>
        }
      >
        {/* Summary Stats - Fixed deprecated valueStyle */}
        <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card size="small" style={{ textAlign: 'center' }}>
              <Statistic 
                value={summary.total} 
                prefix={<BellOutlined />} 
                suffix="Total"
                valueStyle={{ fontSize: 20 }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" style={{ textAlign: 'center' }}>
              <Statistic 
                value={summary.approved} 
                prefix={<CheckCircleOutlined />} 
                valueStyle={{ fontSize: 20, color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" style={{ textAlign: 'center' }}>
              <Statistic 
                value={summary.rejected} 
                prefix={<CloseCircleOutlined />} 
                valueStyle={{ fontSize: 20, color: '#ff4d4f' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" style={{ textAlign: 'center' }}>
              <Statistic 
                value={summary.escalated} 
                prefix={<ExclamationCircleOutlined />} 
                valueStyle={{ fontSize: 20, color: '#faad14' }}
              />
            </Card>
          </Col>
        </Row>

        <Divider style={{ margin: '8px 0' }} />

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
          </div>
        ) : notifications.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No new notifications"
            style={{ marginTop: 40 }}
          />
        ) : (
          <Tabs defaultActiveKey="all" size="small">
            <TabPane tab={`All (${notifications.length})`} key="all">
              <div style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
                {renderNotificationList(notifications)}
              </div>
            </TabPane>
            <TabPane tab={`Approved (${summary.approved})`} key="approved">
              <div style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
                {renderNotificationList(filteredByType('Approved'))}
              </div>
            </TabPane>
            <TabPane tab={`Rejected (${summary.rejected})`} key="rejected">
              <div style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
                {renderNotificationList(filteredByType('Rejected'))}
              </div>
            </TabPane>
            <TabPane tab={`Pending (${summary.pending})`} key="pending">
              <div style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
                {renderNotificationList(filteredByType('Pending'))}
              </div>
            </TabPane>
            <TabPane tab={`Escalated (${summary.escalated})`} key="escalated">
              <div style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
                {renderNotificationList(filteredByType('Escalated'))}
              </div>
            </TabPane>
          </Tabs>
        )}
      </Drawer>

      {/* Notification Detail Modal */}
      <Modal
        title={
          <Space>
            {selectedNotification && getIconByType(selectedNotification.type)}
            <span>Notification Details</span>
            {selectedNotification && getTagByType(selectedNotification.type)}
          </Space>
        }
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>
        ]}
        width={500}
      >
        {selectedNotification && (
          <div>
            <Card size="small" style={{ marginBottom: 16, background: '#f0f5ff' }}>
              <Text strong style={{ fontSize: 16 }}>{selectedNotification.title}</Text>
              <Divider style={{ margin: '12px 0' }} />
              <Paragraph>{selectedNotification.message}</Paragraph>
            </Card>

            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Form">
                <Text strong>{selectedNotification.formName || 'N/A'}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Submission ID">
                <Text code>#{selectedNotification.submissionId}</Text>
              </Descriptions.Item>
              {selectedNotification.actionPerformedBy && (
                <Descriptions.Item label="Action By">
                  <Space>
                    <UserOutlined />
                    <Text>{selectedNotification.actionPerformedBy}</Text>
                    {selectedNotification.actionPerformedByRole && (
                      <Tag>{selectedNotification.actionPerformedByRole}</Tag>
                    )}
                  </Space>
                </Descriptions.Item>
              )}
              {selectedNotification.actionComments && (
                <Descriptions.Item label="Remarks">
                  <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                    {selectedNotification.actionComments}
                  </Paragraph>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Received At">
                {new Date(selectedNotification.createdAt).toLocaleString()}
              </Descriptions.Item>
              {selectedNotification.actionTakenAt && (
                <Descriptions.Item label="Action Taken At">
                  {new Date(selectedNotification.actionTakenAt).toLocaleString()}
                </Descriptions.Item>
              )}
            </Descriptions>

            <Alert
              message="What would you like to do?"
              description={
                <Space style={{ marginTop: 12 }}>
                  <Button 
                    type="primary" 
                    size="small" 
                    icon={<EyeOutlined />}
                    onClick={() => {
                      if (selectedNotification.submissionId) {
                        window.location.href = `/submission/${selectedNotification.submissionId}`;
                      }
                    }}
                  >
                    View Submission
                  </Button>
                  <Button 
                    size="small"
                    onClick={() => setDetailModalVisible(false)}
                  >
                    Dismiss
                  </Button>
                </Space>
              }
              type="info"
              showIcon
              style={{ marginTop: 16 }}
            />
          </div>
        )}
      </Modal>
    </>
  );
};

export default NotificationPanel;